import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Subscription } from 'rxjs';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { StoredContext } from '../../models/context';
import { ContextService } from '../../shared/services/context.service';
import { TaskReminderService } from './task-reminder.service';

@Component({
  selector: 'app-task-reminder',
  templateUrl: './task-reminder.component.html',
  styleUrls: ['./task-reminder.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TaskReminderComponent implements OnInit, OnDestroy {
  @Input() documentId?: string;

  services = [
    {
      id: 'google',
      name: 'Google Calendar',
      iconPath: 'assets/images/google-calendar-logo.svg',
      enabled: true,
      color: '#4285F4',
      action: 'google',
      tooltip: 'task-reminder.tooltip.google-calendar',
    },
    {
      id: 'outlook',
      name: 'Outlook',
      iconPath: 'assets/images/outlook-logo.svg',
      enabled: true,
      color: '#0078D4',
      action: 'outlook',
      tooltip: 'task-reminder.tooltip.outlook',
    },
    {
      id: 'hubspot',
      name: 'HubSpot',
      iconPath: 'assets/images/hubspot-logo.svg',
      enabled: true,
      color: '#FF7A33',
      action: 'hubspot',
      tooltip: 'task-reminder.tooltip.hubspot',
    },
    {
      id: 'salesforce',
      name: 'Salesforce',
      iconPath: 'assets/images/salesforce-logo.svg',
      enabled: true,
      color: '#00A1E0',
      action: 'salesforce',
      tooltip: 'task-reminder.tooltip.salesforce',
    },
  ];

  private currentContext: StoredContext | null = null;
  activeCrmType: 'hubspot' | 'salesforce' | null = null;
  private oauthClosedSub?: Subscription;

  constructor(
    private contextService: ContextService,
    private calendarService: TaskReminderService
  ) {}

  ngOnInit(): void {
    // Listen for OAuth window close and refresh icons/state
    this.oauthClosedSub = this.calendarService.oauthWindowClosed$.subscribe(() => {
      // After the pop-up closes, refresh the main tab to ensure state is up-to-date
      window.location.reload();
    });
    this.refreshCrmVisibility();
  }

  ngOnDestroy(): void {
    this.oauthClosedSub?.unsubscribe();
  }

  private loadCurrentContext(): void {
    const storedContexts = this.contextService.getStoredContexts();
    // Default to the most recent context
    if (storedContexts && storedContexts.length > 0) {
      this.currentContext = storedContexts[storedContexts.length - 1];
      // If a documentId is provided, override with the matching one when available
      if (this.documentId) {
        const matched = storedContexts.find((item: any) => item?.id === this.documentId);
        if (matched) {
          this.currentContext = matched;
        }
      }
    } else {
      this.currentContext = null;
    }
  }

  async onServiceClick(service: any, tooltip?: NgbTooltip): Promise<void> {
    // Close the tooltip immediately when button is clicked
    if (tooltip) {
      tooltip.close();
    }

    this.loadCurrentContext();
    if (!this.currentContext) {
      console.warn('No context available for calendar event creation');
      return;
    }

    await this.calendarService.handleCalendarAction(service.action, this.currentContext);
  }

  shouldShowService(service: any): boolean {
    const isCrm = service.id === 'hubspot' || service.id === 'salesforce';
    if (!isCrm) return true; // Always show Google/Outlook
    // If no active CRM configured, show both CRM buttons to encourage connection
    if (!this.activeCrmType) return true;
    // If an active CRM exists, only show that one
    return service.id === this.activeCrmType;
  }

  private async refreshCrmVisibility(): Promise<void> {
    try {
      const integrations = await this.calendarService.getUserCrmIntegrations();
      const active = integrations.find(i => i.status === 'active');
      this.activeCrmType = active ? active.type : null;
    } catch (error) {
      console.warn('Failed to fetch CRM integrations for visibility', error);
      this.activeCrmType = null;
    }
  }
}
